import type { AxiosResponse } from 'axios'

import environment from '@/environments/environment.dev'

import {
  FAPI_SHARED_WISHLIST_ENDPOINT,
  FAPI_WISHLIST_ENDPOINT,
} from '../libs/constants/b2bFapiApiEndpoints'
import HttpClient from './apiHttpClient'

const apiClient = new HttpClient(environment.shopFAPIUrl)

export const WishlistService = {
  async getWishlistOverview({
    ownerId,
    searchQuery = '',
    pageNumber = 1,
    pageSize = 6,
    type,
  }: {
    ownerId: string
    searchQuery?: string
    pageNumber?: number
    pageSize?: number
    type?: 'owner' | 'shared'
  }): Promise<AxiosResponse<unknown>> {
    if (!ownerId) {
      throw new Error('missing ownerId')
    }

    return apiClient.get(`${FAPI_WISHLIST_ENDPOINT}/overview/${ownerId}`, {
      params: {
        searchQuery,
        pageNumber,
        pageSize,
        ...(type !== undefined && { type }),
      },
    })
  },

  async getWishlistsList({ ownerId }): Promise<AxiosResponse<unknown>> {
    return apiClient.get(`${FAPI_WISHLIST_ENDPOINT}/${ownerId}`)
  },

  async createWishlist({ newWishlistName }) {
    return apiClient.post(`${FAPI_WISHLIST_ENDPOINT}/create`, {
      params: {
        wishlistName: newWishlistName,
      },
    })
  },

  async addProductToWishlist({
    ownerId,
    materialNumber,
    quantity,
    wishlistId,
  }): Promise<AxiosResponse<unknown>> {
    return apiClient.put(`${FAPI_WISHLIST_ENDPOINT}/${wishlistId}/items`, {
      params: {
        ownerId,
        materialNumber,
        quantity,
      },
    })
  },

  async getWishlistDetail({
    wishlistOwnerId,
    wishlistId,
    pageNumber = 1,
    pageSize = 10,
    searchQuery,
  }): Promise<AxiosResponse<unknown>> {
    return apiClient.get(
      `${FAPI_WISHLIST_ENDPOINT}/${wishlistOwnerId}/detail/${wishlistId}`,
      {
        params: {
          pageNumber,
          pageSize,
          searchQuery,
        },
      },
    )
  },

  async addWishlistProductsToCart({
    wishlistOwnerId,
    wishlistId,
    sessionId,
  }): Promise<AxiosResponse<unknown>> {
    return apiClient.post(
      `${FAPI_WISHLIST_ENDPOINT}/${wishlistOwnerId}/add-to-cart`,
      {
        params: { wishlistId, sessionId },
      },
    )
  },

  async updateWishlistProductQuantity({
    wishlistOwnerId,
    wishlistId,
    productData,
  }): Promise<AxiosResponse<unknown>> {
    return apiClient.put(
      `${FAPI_WISHLIST_ENDPOINT}/${wishlistOwnerId}/detail/${wishlistId}/update`,
      {
        body: productData,
      },
    )
  },

  async removeProductInWishlist({
    wishlistOwnerId,
    wishlistId,
    materialNumber,
  }): Promise<AxiosResponse<unknown>> {
    return apiClient.delete(`${FAPI_WISHLIST_ENDPOINT}/${wishlistId}/items`, {
      params: {
        ownerId: wishlistOwnerId,
        materialNumber,
      },
    })
  },

  async deleteWishlist({
    wishlistOwnerId,
    wishlistId,
  }): Promise<AxiosResponse<unknown>> {
    return apiClient.delete(`${FAPI_WISHLIST_ENDPOINT}`, {
      params: {
        ownerId: wishlistOwnerId,
        wishlistId,
      },
    })
  },

  async renameWishlist({
    ownerId,
    wishlistId,
    newName,
  }): Promise<AxiosResponse<unknown>> {
    return apiClient.put(
      `${FAPI_WISHLIST_ENDPOINT}/${ownerId}/rename/${wishlistId}`,
      {
        params: {
          newName,
        },
      },
    )
  },

  async getSearchSuggestions(query: string) {
    return apiClient.get(`${FAPI_SHARED_WISHLIST_ENDPOINT}/searchusers/`, {
      params: {
        emailOrUserName: query,
      },
    })
  },

  async shareWishlistToUsers({ wishlistId, userIds }) {
    return apiClient.post(
      `${FAPI_SHARED_WISHLIST_ENDPOINT}/wishlists/${wishlistId}/users`,
      {
        body: userIds,
        params: {
          wishlistId,
        },
      },
    )
  },

  async relocateWishlists(
    items: Array<{ ownerId: string; wishlistId: string; order: number }>,
  ): Promise<AxiosResponse<unknown>> {
    return apiClient.put(`${FAPI_WISHLIST_ENDPOINT}/relocate`, {
      body: items,
    })
  },

  async relocateWishlistItems(
    ownerId: string,
    wishlistId: string,
    items: Array<{ materialNumber: string; order: number }>,
  ): Promise<AxiosResponse<unknown>> {
    return apiClient.put(
      `${FAPI_WISHLIST_ENDPOINT}/${ownerId}/detail/${wishlistId}/relocate-wishlist-item`,
      { body: items },
    )
  },

  async getWishlistSharedDetail(wishlistId: string) {
    return apiClient.get(
      `${FAPI_SHARED_WISHLIST_ENDPOINT}/${wishlistId}/details`,
    )
  },

  async shareWishlistToCompany(wishlistId: string) {
    return apiClient.put(
      `${FAPI_SHARED_WISHLIST_ENDPOINT}/wishlists/${wishlistId}/company`,
      {
        params: {
          wishlistId,
        },
      },
    )
  },

  async withdrawSharingWishlistToCompany(wishlistId: string) {
    return apiClient.delete(
      `${FAPI_SHARED_WISHLIST_ENDPOINT}/wishlists/${wishlistId}/company`,
      {
        params: {
          wishlistId,
        },
      },
    )
  },
}
